import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Box, MenuItem, TextField, Chip, Stack, Alert,
  CircularProgress, IconButton, Collapse, InputAdornment, Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
  Star as StarIcon,
  FlashOn as FlashOnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  VerifiedUser as VerifiedIcon,
  Close as CloseIcon,
  TravelExplore as TravelIcon
} from '@mui/icons-material';
import { checkDuplicateInquiries, createInquiry } from '../../api/inquiryAPI';
import { verifyInquiryLead } from '../../api/verifiedLeadsAPI';

const LEAD_SOURCE_OPTIONS = [
  { value: 'WhatsApp', label: '💬 WhatsApp' },
  { value: 'Offline', label: '🏢 Offline / Direct Call' },
  { value: 'Walk-in', label: '🚶 Walk-in' },
  { value: 'Instagram', label: '📸 Instagram' },
  { value: 'Facebook', label: '👥 Facebook' },
  { value: 'Website', label: '🌐 Website' },
  { value: 'sikkimtourism.in', label: '🌐 sikkimtourism.in' },
  { value: 'sandakphutrek.com', label: '🌐 sandakphutrek.com' },
  { value: 'easotrip.com', label: '🌐 easotrip.com' },
  { value: 'Other', label: '🏷️ Other Source' }
];

const INITIAL_FORM = {
  name: '',
  phone: '',
  country_code: '+91',
  email: '',
  source: 'WhatsApp',
  address: '',
  destination: '',
  arrival_date: '',
  pax: 2,
  kids_above_5: '',
  message: ''
};

const QuickInsertInquiryDialog = ({ open, onClose, onSuccess, showSnackbar }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [phoneCheckResult, setPhoneCheckResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset form when opened
  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setCheckingPhone(false);
      setPhoneCheckResult(null);
      setShowHistory(false);
      setSaving(false);
    }
  }, [open]);

  // Real-time phone check across Inquiry, Query, and Guest models
  useEffect(() => {
    if (!open) return;

    const rawPhone = (form.phone || '').trim();
    const cleanDigits = rawPhone.replace(/\D/g, '');

    if (cleanDigits.length < 10) {
      setPhoneCheckResult(null);
      setCheckingPhone(false);
      return;
    }

    setCheckingPhone(true);
    const timer = setTimeout(async () => {
      try {
        const res = await checkDuplicateInquiries(
          form.email || '',
          cleanDigits,
          null,
          form.destination || ''
        );
        if (res && res.success) {
          setPhoneCheckResult(res);
        } else {
          setPhoneCheckResult(null);
        }
      } catch (err) {
        console.error('Real-time phone check error:', err);
        setPhoneCheckResult(null);
      } finally {
        setCheckingPhone(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [form.phone, form.destination, form.email, open]);

  // Auto-fill known contact details from previous models
  const handleAutoFillDetails = () => {
    if (!phoneCheckResult?.matchedCustomer) return;
    const mc = phoneCheckResult.matchedCustomer;
    setForm(prev => ({
      ...prev,
      name: mc.name || prev.name,
      email: mc.email || prev.email,
      address: mc.address || prev.address,
      country_code: mc.country_code || prev.country_code || '+91'
    }));
    if (showSnackbar) {
      showSnackbar('Loaded customer details from existing profile!', 'info');
    }
  };

  // Submit and create inquiry (+ optional auto-verify)
  const handleSubmit = async (andVerify = false) => {
    const trimmedName = (form.name || '').trim();
    const rawPhone = (form.phone || '').trim();
    const cleanDigits = rawPhone.replace(/\D/g, '');
    const trimmedSource = (form.source || '').trim();

    if (!trimmedName) {
      if (showSnackbar) showSnackbar('Guest name is required', 'error');
      return;
    }
    if (!cleanDigits || cleanDigits.length < 10) {
      if (showSnackbar) showSnackbar('Please enter a valid 10-digit phone number', 'error');
      return;
    }
    if (!trimmedSource) {
      if (showSnackbar) showSnackbar('Lead source is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        guest_name: trimmedName,
        guest_phone: cleanDigits.slice(-10),
        guest_country_code: form.country_code || '+91',
        guest_email: (form.email || '').trim(),
        guest_address: (form.address || '').trim(),
        lead_source: trimmedSource,
        destination: (form.destination || '').trim(),
        arrival_date: form.arrival_date || undefined,
        pax: form.pax ? Number(form.pax) : 1,
        kids_above_5: form.kids_above_5 ? Number(form.kids_above_5) : 0,
        guest_message: (form.message || '').trim()
      };

      const res = await createInquiry(payload);
      const newInquiry = res.data;

      if (andVerify && newInquiry?._id) {
        try {
          await verifyInquiryLead(newInquiry._id);
          if (showSnackbar) {
            showSnackbar('Inquiry created & auto-verified! Contact updated across all models.', 'success');
          }
        } catch (vErr) {
          if (showSnackbar) {
            showSnackbar('Inquiry created, but auto-verification notice: ' + (vErr.message || vErr), 'warning');
          }
        }
      } else {
        if (showSnackbar) {
          showSnackbar('Inquiry successfully created & contact synced across Inquiry, Query & Guest models!', 'success');
        }
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to create inquiry:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to create inquiry';
      if (showSnackbar) showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const modelMatches = phoneCheckResult?.modelMatches || {};
  const matchedCustomer = phoneCheckResult?.matchedCustomer || null;
  const duplicates = phoneCheckResult?.duplicates || [];

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            p: 1, borderRadius: 2, bgcolor: '#fff7ed', color: '#ea580c',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(234, 88, 12, 0.2)'
          }}>
            <FlashOnIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b' }}>
              Quick Insert Inquiry
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Instant lead capture for WhatsApp, Calls & Walk-ins with real-time multi-model duplicate checking
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving} sx={{ color: '#64748b' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* ── Section 1: Phone input with live model check ── */}
        <Box sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2.5, border: '1px solid #cbd5e1' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#334155', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            📱 Phone Number Check
            <Typography variant="caption" color="text.secondary" fontWeight={400}>
              (Checks Inquiry, Query & Guest models in real-time)
            </Typography>
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField
              select
              size="small"
              value={form.country_code}
              onChange={(e) => setForm({ ...form, country_code: e.target.value })}
              sx={{ width: { xs: '100%', sm: '120px' }, bgcolor: '#fff' }}
            >
              <MenuItem value="+91">+91 (IN)</MenuItem>
              <MenuItem value="+1">+1 (US/CA)</MenuItem>
              <MenuItem value="+44">+44 (UK)</MenuItem>
              <MenuItem value="+971">+971 (UAE)</MenuItem>
              <MenuItem value="+61">+61 (AU)</MenuItem>
            </TextField>

            <TextField
              size="small"
              fullWidth
              required
              label="Phone Number"
              placeholder="e.g. 9832278258"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {checkingPhone && <CircularProgress size={18} color="inherit" />}
                    {!checkingPhone && phoneCheckResult && (
                      phoneCheckResult.isPreviousGuest ? (
                        <WarningIcon color="warning" fontSize="small" />
                      ) : (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )
                    )}
                  </InputAdornment>
                )
              }}
              sx={{ bgcolor: '#fff' }}
            />
          </Stack>

          {/* ── Multi-Model Results Banner ── */}
          {phoneCheckResult && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} mb={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {phoneCheckResult.isLoyalGuest ? (
                    <Chip
                      icon={<StarIcon sx={{ color: '#d97706 !important' }} />}
                      label="⭐ Loyal Guest (Confirmed Bookings)"
                      size="small"
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }}
                    />
                  ) : phoneCheckResult.isDuplicate ? (
                    <Chip
                      icon={<WarningIcon sx={{ color: '#ea580c !important' }} />}
                      label="⚠️ Duplicate Inquiry (Same Destination)"
                      size="small"
                      sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 700 }}
                    />
                  ) : phoneCheckResult.isMultiple ? (
                    <Chip
                      label="🔁 Multiple Inquiries / Trips"
                      size="small"
                      sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}
                    />
                  ) : (
                    <Chip
                      icon={<CheckCircleIcon sx={{ color: '#16a34a !important' }} />}
                      label="✨ New Customer (No Prior Records)"
                      size="small"
                      sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Total previous records: <strong>{phoneCheckResult.totalPreviousTrips || 0}</strong>
                  </Typography>
                </Box>

                {matchedCustomer && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<FlashOnIcon />}
                    onClick={handleAutoFillDetails}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                  >
                    Auto-fill Known Profile
                  </Button>
                )}
              </Stack>

              {/* Multi-model presence indicators */}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, my: 1 }}>
                <Chip
                  size="small"
                  label={`Inquiry Model: ${modelMatches.inquiry ? `Found (${modelMatches.inquiryCount || 1})` : 'None'}`}
                  color={modelMatches.inquiry ? 'primary' : 'default'}
                  variant={modelMatches.inquiry ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
                <Chip
                  size="small"
                  label={`Query Model: ${modelMatches.query ? `Found (${modelMatches.queryCount || 1} quotes)` : 'None'}`}
                  color={modelMatches.query ? 'secondary' : 'default'}
                  variant={modelMatches.query ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
                <Chip
                  size="small"
                  label={`Guest Model: ${modelMatches.guest ? `Found (${modelMatches.guestCount || 1} guest)` : 'None'}`}
                  color={modelMatches.guest ? 'warning' : 'default'}
                  variant={modelMatches.guest ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              </Stack>

              {matchedCustomer && (
                <Alert severity="info" sx={{ py: 0.5, px: 1.5, my: 1, borderRadius: 1.5, fontSize: '0.8rem' }}>
                  <strong>Known Profile on File:</strong> Name: <em>{matchedCustomer.name || 'Not set'}</em> | Email: <em>{matchedCustomer.email || 'None'}</em> | Address: <em>{matchedCustomer.address || 'None'}</em>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Saving this inquiry will automatically synchronize updated contact info across all 3 models.
                  </Typography>
                </Alert>
              )}

              {/* Expandable past history */}
              {duplicates.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setShowHistory(!showHistory)}
                    endIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, minHeight: 'auto', color: '#64748b' }}
                  >
                    {showHistory ? 'Hide Previous Records' : `View ${duplicates.length} Previous Records`}
                  </Button>
                  <Collapse in={showHistory}>
                    <Stack spacing={1} sx={{ mt: 1, maxHeight: 150, overflowY: 'auto', p: 1, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                      {duplicates.map((dup, i) => (
                        <Box key={dup.id || i} sx={{ p: 0.8, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontWeight={700} color="primary.main">
                              {dup.destination || 'General Trip'}
                            </Typography>
                            <Chip label={dup.source || dup.status || 'Past Record'} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Stack>
                          <Stack direction="row" spacing={2} sx={{ mt: 0.3 }}>
                            {dup.quotation_no && <Typography variant="caption"><strong>Quote:</strong> {dup.quotation_no}</Typography>}
                            <Typography variant="caption" color="text.secondary">
                              {dup.date ? new Date(dup.date).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ── Section 2: Contact Information ── */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#334155', mb: 1.5 }}>
            👤 Contact Information
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                required
                fullWidth
                size="small"
                label="Full Name"
                placeholder="e.g. Rahul Sharma"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email Address"
                placeholder="e.g. rahul@example.com (optional)"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                required
                fullWidth
                size="small"
                label="Lead Source"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              >
                {LEAD_SOURCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                size="small"
                label="Destination / Trip Location"
                placeholder="e.g. Sikkim & Darjeeling"
                value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })}
              />
            </Stack>

            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Address"
              placeholder="e.g. Flat 402, Green Valley Apartments, Kolkata, West Bengal"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              helperText="Address will automatically be updated across Inquiry, Query, and Guest records"
            />
          </Stack>
        </Box>

        <Divider />

        {/* ── Section 3: Trip Details (Optional) ── */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#334155', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TravelIcon fontSize="small" color="action" /> Trip Specifics (Optional)
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Estimated Arrival Date"
                value={form.arrival_date}
                onChange={(e) => setForm({ ...form, arrival_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Number of Pax"
                value={form.pax}
                onChange={(e) => setForm({ ...form, pax: e.target.value })}
                inputProps={{ min: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Children (Age 5 to 11 yr)"
                placeholder="0"
                value={form.kids_above_5}
                onChange={(e) => setForm({ ...form, kids_above_5: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Stack>

            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Notes / Special Requirements"
              placeholder="e.g. Interested in 4-star hotels, vegetarian meal plan, luxury cab..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit(false)}
          variant="outlined"
          color="primary"
          disabled={saving}
          sx={{ fontWeight: 600, px: 2.5 }}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Inquiry'}
        </Button>
        <Button
          onClick={() => handleSubmit(true)}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={!saving && <VerifiedIcon />}
          sx={{
            fontWeight: 700,
            px: 3,
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            boxShadow: '0 2px 8px rgba(234, 88, 12, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
            }
          }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save & Verify Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickInsertInquiryDialog;
